/**
 * ScriptAdherenceTab Component
 *
 * Displays script adherence evaluation results for a record.
 * Compact styling matching AnalysisPanel v2 and RecordDetailInline.
 */

import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  MinusCircle,
} from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface StepEvaluationResult {
  stepId: string;
  stepNumber: number;
  stepName: string;
  isCompleted: boolean;
  /** Absent on older evaluations, where every step counted. */
  countsTowardScore?: boolean;
  score: number;
  matchedRequired: number;
  totalRequired: number;
  matchedOptional: number;
  totalOptional: number;
  forbiddenDetected: boolean;
  firstOccurrenceTimestamp?: string;
  evidence?: string;
}

interface MatchedPhrase {
  phraseId: string;
  phraseText: string;
  /** Absent on evaluations stored before the field existed — see stepMatches. */
  stepId?: string;
  /** A matched `forbidden` criterion is a violation, not an achievement. */
  phraseType?: 'required' | 'optional' | 'forbidden';
  matchedText: string;
  similarityScore: number;
  timestamp?: string;
  speaker: 'agent' | 'customer';
}

interface MissedPhrase {
  phraseId: string;
  phraseText: string;
  stepId: string;
  stepName: string;
  isRequired: boolean;
}

interface OutOfOrderStep {
  stepId: string;
  stepName: string;
  expectedPosition: number;
  actualPosition: number;
  timestamp?: string;
}

export interface ScriptAdherenceData {
  id: string;
  analysisId: string;
  recordId: string;
  scriptId: string;
  scriptName?: string;
  scriptVersion: number;
  adherenceScore: number;
  stepsCompleted: number;
  stepsTotal: number;
  stepsInOrder: number;
  orderScore?: number;
  stepResults: StepEvaluationResult[];
  matchedPhrases: MatchedPhrase[];
  missedPhrases: MissedPhrase[];
  outOfOrderSteps: OutOfOrderStep[];
  evaluationModel?: string;
  evaluationTokensIn?: number;
  evaluationTokensOut?: number;
  evaluationCostCents?: number;
  createdAt: string;
}

interface ScriptAdherenceTabProps {
  adherence: ScriptAdherenceData | null;
  loading?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function scoreTextColor(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-warning';
  if (score >= 40) return 'text-caution';
  return 'text-danger';
}

// ============================================================================
// StepRow — compact expandable row for each step
// ============================================================================

function StepRow({
  step,
  isExpanded,
  onToggle,
  matchedPhrases,
  missedPhrases,
}: {
  step: StepEvaluationResult;
  isExpanded: boolean;
  onToggle: () => void;
  matchedPhrases: MatchedPhrase[];
  missedPhrases: MissedPhrase[];
}) {
  const { t } = useTranslation();

  // A match belongs to the step its criterion belongs to — nothing else.
  // Evaluations stored before `stepId` existed simply show no matches on the
  // step; the old substring heuristic that stood here could hand a step its
  // neighbour's match and present the guess as verified.
  // A matched FORBIDDEN criterion lives in the same list — that is how a
  // violation is recorded — so it must not appear under "Matched", where the
  // green heading would read it as an achievement.
  const stepMatchedPhrases = matchedPhrases.filter(
    (p) => p.stepId === step.stepId && p.phraseType !== 'forbidden'
  );
  const stepForbiddenPhrases = matchedPhrases.filter(
    (p) => p.stepId === step.stepId && p.phraseType === 'forbidden'
  );
  const stepMissedPhrases = missedPhrases.filter((p) => p.stepId === step.stepId);

  // A step the script did not require, on a conversation where it never came
  // up, was not failed — it was not owed. It is out of the score and out of
  // the tally above, so it must not read as a failure here either.
  const notOwed = step.countsTowardScore === false;

  return (
    <div
      className={cn(
        'border rounded-md overflow-hidden transition-colors',
        notOwed
          ? 'border-border bg-bg-subtle'
          : step.isCompleted
            ? 'border-border bg-success-dim'
            : step.forbiddenDetected
              ? 'border-danger bg-danger-dim'
              : 'border-border bg-bg-surface'
      )}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-bg-accent transition-colors text-left"
      >
        {notOwed ? (
          <MinusCircle className="h-3.5 w-3.5 text-fg-subtle shrink-0" />
        ) : step.isCompleted ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
        ) : step.forbiddenDetected ? (
          <AlertTriangle className="h-3.5 w-3.5 text-danger shrink-0" />
        ) : (
          <XCircle className="h-3.5 w-3.5 text-fg-muted shrink-0" />
        )}

        <span className="text-xs text-fg-muted shrink-0">{step.stepNumber}.</span>
        <span
          className={cn(
            'text-xs flex-1 truncate',
            notOwed ? 'text-fg-subtle' : 'text-fg'
          )}
        >
          {step.stepName}
        </span>

        {notOwed ? (
          <span className="text-2xs text-fg-subtle shrink-0">
            {t('scripts.stepNotRequired', 'not required')}
          </span>
        ) : (
          <span className="text-2xs text-fg-muted shrink-0">
            {step.matchedRequired}/{step.totalRequired}
          </span>
        )}

        <span
          className={cn(
            'font-mono text-xs font-semibold w-8 text-right shrink-0',
            notOwed ? 'text-fg-subtle' : scoreTextColor(step.score)
          )}
        >
          {notOwed ? '—' : step.score}
        </span>

        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-fg-muted shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-fg-muted shrink-0" />
        )}
      </button>

      {isExpanded && (
        <div className="px-2.5 pb-2 space-y-2 border-t border-border pt-2">
          {step.evidence && (
            <p className="text-2xs text-fg-muted italic bg-bg-elevated rounded px-2 py-1.5">
              &ldquo;{step.evidence}&rdquo;
            </p>
          )}

          {stepMatchedPhrases.length > 0 && (
            <div className="space-y-1">
              <div className="text-2xs font-medium text-success-fg flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {t('scripts.matchedPhrases', 'Matched Phrases')}
              </div>
              {stepMatchedPhrases.map((phrase) => (
                <div
                  key={phrase.phraseId}
                  className="text-2xs bg-success-dim border border-border rounded px-2 py-1"
                >
                  <span className="text-fg-muted">{phrase.phraseText}</span>
                  <span className="text-fg ml-1">
                    &rarr; &ldquo;{phrase.matchedText}&rdquo;
                  </span>
                  <span className="text-fg-subtle ml-1">
                    ({Math.round(phrase.similarityScore * 100)}%)
                  </span>
                </div>
              ))}
            </div>
          )}

          {stepForbiddenPhrases.length > 0 && (
            <div className="space-y-1">
              <div className="text-2xs font-medium text-danger-fg flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {t('scripts.forbiddenDetected', 'Forbidden detected')}
              </div>
              {stepForbiddenPhrases.map((phrase) => (
                <div
                  key={phrase.phraseId}
                  className="text-2xs bg-danger-dim border border-danger rounded px-2 py-1"
                >
                  <span className="text-fg-muted">{phrase.phraseText}</span>
                  <span className="text-fg ml-1">
                    &rarr; &ldquo;{phrase.matchedText}&rdquo;
                  </span>
                </div>
              ))}
            </div>
          )}

          {stepMissedPhrases.length > 0 && (
            <div className="space-y-1">
              <div className="text-2xs font-medium text-danger-fg flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                {t('scripts.missedPhrases', 'Missed Phrases')}
              </div>
              {stepMissedPhrases.map((phrase) => (
                <div
                  key={phrase.phraseId}
                  className={cn(
                    'text-2xs border rounded px-2 py-1',
                    phrase.isRequired
                      ? 'bg-danger-dim border-danger'
                      : 'bg-warning-dim border-border'
                  )}
                >
                  <span className="text-fg">{phrase.phraseText}</span>
                  <span
                    className={cn(
                      'ml-1',
                      phrase.isRequired ? 'text-danger-fg' : 'text-warning-fg'
                    )}
                  >
                    (
                    {phrase.isRequired
                      ? t('scripts.required', 'required')
                      : t('scripts.optional', 'optional')}
                    )
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ScriptAdherenceTab({ adherence, loading }: ScriptAdherenceTabProps) {
  const { t } = useTranslation();
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse h-6 bg-bg-muted rounded" />
        <div className="animate-pulse h-20 bg-bg-muted rounded" />
      </div>
    );
  }

  if (!adherence) {
    return null;
  }

  const sortedSteps = [...adherence.stepResults].sort(
    (a, b) => a.stepNumber - b.stepNumber
  );

  const missedRequired = adherence.missedPhrases.filter((p) => p.isRequired);

  return (
    <div className="space-y-3">
      {/* Stat tiles — Completed / In Order / Order score */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md bg-bg-surface px-2 py-2 text-center">
          <div className="font-mono text-sm font-semibold text-fg">
            {adherence.stepsCompleted}/{adherence.stepsTotal}
          </div>
          <div className="mt-0.5 text-2xs uppercase tracking-wide text-fg-subtle">
            {t('scripts.stepsCompleted', 'Steps')}
          </div>
        </div>
        <div className="rounded-md bg-bg-surface px-2 py-2 text-center">
          <div className="font-mono text-sm font-semibold text-fg">
            {adherence.stepsInOrder}/{adherence.stepsTotal}
          </div>
          <div className="mt-0.5 text-2xs uppercase tracking-wide text-fg-subtle">
            {t('scripts.stepsInOrder', 'In Order')}
          </div>
        </div>
        <div className="rounded-md bg-bg-surface px-2 py-2 text-center">
          {adherence.orderScore !== undefined ? (
            <>
              <div
                className={cn(
                  'font-mono text-sm font-semibold',
                  scoreTextColor(adherence.orderScore)
                )}
              >
                {adherence.orderScore}%
              </div>
              <div className="mt-0.5 text-2xs uppercase tracking-wide text-fg-subtle">
                {t('scripts.orderScore', 'Order')}
              </div>
            </>
          ) : (
            <div className="text-sm text-fg-faint">—</div>
          )}
        </div>
      </div>

      {/* Out of Order Warning */}
      {adherence.outOfOrderSteps.length > 0 && (
        <div className="flex items-start gap-2 p-2 bg-warning-dim rounded text-2xs">
          <AlertTriangle className="h-3 w-3 text-warning shrink-0 mt-px" />
          <div className="space-y-0.5">
            {adherence.outOfOrderSteps.map((step) => (
              <div key={step.stepId} className="text-fg-muted">
                <span className="text-fg">{step.stepName}</span>
                {': '}
                {t('scripts.expectedPosition', 'expected')} {step.expectedPosition}
                {' → '}
                {t('scripts.actualPosition', 'actual')} {step.actualPosition}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Steps Breakdown */}
      <div className="space-y-1.5">
        <p className="text-xs text-fg-subtle uppercase tracking-wider font-medium">
          {t('scripts.stepByStepBreakdown', 'Steps')}
        </p>
        <div className="space-y-1">
          {sortedSteps.map((step) => (
            <StepRow
              key={step.stepId}
              step={step}
              isExpanded={expandedSteps.has(step.stepId)}
              onToggle={() => toggleStep(step.stepId)}
              matchedPhrases={adherence.matchedPhrases}
              missedPhrases={adherence.missedPhrases}
            />
          ))}
        </div>
      </div>

      {/* Missed Required Phrases Summary */}
      {missedRequired.length > 0 && (
        <div className="space-y-1">
          <p className="text-2xs font-medium text-danger flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            {t('scripts.missedRequiredPhrases', 'Missed Required')} (
            {missedRequired.length})
          </p>
          <div className="space-y-0.5">
            {missedRequired.map((phrase) => (
              <div
                key={phrase.phraseId}
                className="text-2xs bg-danger-dim border border-danger rounded px-2 py-1"
              >
                <span className="text-fg">{phrase.phraseText}</span>
                <span className="text-fg-subtle ml-1">({phrase.stepName})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meta: script info. The evaluation model is deliberately not shown —
          LLM model names are hidden from analysis surfaces (user decision,
          UX audit 2026-07-16) */}
      {adherence.scriptName && (
        <div className="flex items-center gap-2 p-2 px-3 bg-bg-surface rounded-md text-xs">
          <span className="text-fg-muted">
            {t('scripts.script', 'Script rules')}:{' '}
            <span className="text-fg">{adherence.scriptName}</span>
            {adherence.scriptVersion > 1 && (
              <span className="text-fg-subtle ml-1">v{adherence.scriptVersion}</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
